import { Trans, useLingui } from "@lingui/react/macro";
import { readBoundedJsonResponse } from "@rakazo/core";
import { Button } from "@rakazo/ui-web";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useIdentityConfirmation } from "../components/ConfirmIdentityDialog";
import { authClient } from "../lib/auth";

type Status = { state: "expired" | "sign-in" | "switch" | "bind"; email?: string };

export function PortalConnectPage() {
  const { t } = useLingui();
  const [status, setStatus] = useState<Status | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const { withIdentityConfirmation, identityDialog } = useIdentityConfirmation();
  useEffect(() => {
    const controller = new AbortController();
    if (new URLSearchParams(window.location.search).has("error")) {
      setStatus({ state: "expired" });
      return;
    }
    void fetch("/api/auth/portal/status", { cache: "no-store", signal: controller.signal })
      .then(async (response) => {
        if (!response.ok) throw new Error();
        const data = await readBoundedJsonResponse<Status>(response, 4096);
        if (
          !["expired", "sign-in", "switch", "bind"].includes(data.state) ||
          (data.state !== "expired" && typeof data.email !== "string")
        )
          throw new Error();
        setStatus(data);
      })
      .catch(() => {
        if (!controller.signal.aborted) setStatus({ state: "expired" });
      });
    return () => controller.abort();
  }, []);

  async function bind() {
    if (busy || !confirmed || !status?.email) return;
    setBusy(true);
    setError("");
    try {
      await withIdentityConfirmation(async () => {
        const response = await fetch("/api/auth/portal/bind", {
          method: "POST",
          cache: "no-store",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ email: status.email }),
        });
        if (!response.ok) {
          const body = await readBoundedJsonResponse<{ code?: string }>(response, 4096);
          throw Object.assign(
            new Error(t`Could not connect this account. Restart from the employee portal.`),
            { code: body.code },
          );
        }
      });
      window.location.assign("/app");
    } catch {
      setError(t`Could not connect this account. Restart from the employee portal.`);
    } finally {
      setBusy(false);
    }
  }
  async function restart() {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const result = await authClient.signOut();
      if (result.error) throw new Error();
      window.location.assign("/api/auth/portal/start");
    } catch {
      setError(t`Could not sign out. Try again.`);
      setBusy(false);
    }
  }
  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col justify-center gap-6 px-6 py-12">
      <h1 className="text-2xl font-semibold">
        <Trans>Connect employee portal</Trans>
      </h1>
      {!status ? (
        <p role="status" className="text-muted-foreground">
          <Trans>Loading…</Trans>
        </p>
      ) : null}
      {status?.email ? (
        <p className="break-all text-sm text-muted-foreground">{status.email}</p>
      ) : null}
      {status?.state === "sign-in" ? (
        <>
          <p className="text-sm">
            <Trans>Sign in to your existing account to link this employee identity.</Trans>
          </p>
          <Link
            className="font-medium underline underline-offset-4"
            to="/sign-in?next=/portal/connect"
          >
            <Trans>Sign in</Trans>
          </Link>
        </>
      ) : null}
      {status?.state === "bind" ? (
        <>
          <label className="flex items-start gap-3 text-sm">
            <input
              type="checkbox"
              className="mt-1 accent-primary"
              checked={confirmed}
              disabled={busy}
              onChange={(event) => setConfirmed(event.target.checked)}
            />
            <Trans>Link the employee identity shown above to my signed-in account.</Trans>
          </label>
          <Button disabled={!confirmed || busy} aria-busy={busy} onClick={() => void bind()}>
            <Trans>Confirm and connect</Trans>
          </Button>
        </>
      ) : null}
      {status?.state === "switch" ? (
        <>
          <p className="text-sm">
            <Trans>
              You are signed in to a different account. Sign out before continuing with this
              employee identity.
            </Trans>
          </p>
          <Button disabled={busy} aria-busy={busy} onClick={() => void restart()}>
            <Trans>Sign out and restart</Trans>
          </Button>
        </>
      ) : null}
      {status?.state === "expired" ? (
        <>
          <p role="alert" className="text-sm">
            <Trans>This connection could not be completed. Restart from the employee portal.</Trans>
          </p>
          <a className="font-medium underline underline-offset-4" href="/api/auth/portal/start">
            <Trans>Restart connection</Trans>
          </a>
        </>
      ) : null}
      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
      {identityDialog}
    </main>
  );
}
